import React, { useMemo, useEffect, useState } from 'react';
import { ChevronDown, SlidersHorizontal, Download, ArrowUp, ArrowDown, RotateCcw, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Tabs, TabsList, TabsTrigger } from '../ui/tabs';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { useAppSelector, useAppDispatch } from '../../redux/store';
import { selectDocumentSearchAssistantMessage } from '../../redux/selectors/documentSearchSelectors';
import {
  setRelevanceFilter,
  setContentTypeFilter,
  setConfidenceRange,
  setKnowledgebaseFilter,
  setScoreThreshold,
  setSortBy,
  toggleSortDirection,
  resetFilters,
  getFilteredAndSortedResults
} from '../../redux/slices/documentSearchSlice';
import { aggregateSearchResults } from '../../utils/aggregateSearchResults';
import { useFetchRelevantDocumentsMutation } from '../../common/backend-api/backend-api';
import DocumentSearchResultItem from './DocumentSearchResultItem';

// ─── Multi-select dropdown with chip display ────────────────────────────────

interface MultiSelectOption { value: string; label: string }

interface MultiSelectProps {
  placeholder: string;
  options: MultiSelectOption[];
  selected: string[];
  onToggle: (value: string) => void;
}

function MultiSelect({ placeholder, options, selected, onToggle }: MultiSelectProps) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className="w-full min-h-9 px-3 py-1.5 text-left rounded-lg border border-border bg-background text-sm flex flex-wrap gap-1 items-center hover:border-gold/50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold/40">
          {selected.length > 0 ? (
            selected.map(v => {
              const opt = options.find(o => o.value === v);
              return (
                <span key={v} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-gold/10 text-gold text-xs font-medium">
                  {opt?.label || v}
                  <span
                    role="button"
                    aria-label={`Remove ${opt?.label || v}`}
                    onPointerDown={(e) => { e.preventDefault(); e.stopPropagation(); onToggle(v); }}
                    className="hover:opacity-70 cursor-pointer"
                  >
                    <X size={10} />
                  </span>
                </span>
              );
            })
          ) : (
            <span className="text-muted-foreground">{placeholder}</span>
          )}
          <ChevronDown size={14} className="ml-auto text-muted-foreground shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-52 p-2 bg-card border-border" align="start">
        {options.map(opt => (
          <label
            key={opt.value}
            className="flex items-center gap-2.5 px-2 py-1.5 rounded-md hover:bg-secondary cursor-pointer text-sm text-foreground select-none"
          >
            <Checkbox
              checked={selected.includes(opt.value)}
              onCheckedChange={() => onToggle(opt.value)}
            />
            {opt.label}
          </label>
        ))}
      </PopoverContent>
    </Popover>
  );
}

// ─── Range slider (dual handle) ─────────────────────────────────────────────

interface RangeSliderProps {
  min: number;
  max: number;
  step: number;
  value: [number, number];
  onChange: (value: [number, number]) => void;
}

function RangeSlider({ min, max, step, value, onChange }: RangeSliderProps) {
  const pct = (v: number) => ((v - min) / (max - min)) * 100;

  return (
    <div className="relative h-5 flex items-center">
      <div className="absolute inset-x-0 h-1.5 rounded-full bg-secondary">
        <div
          className="absolute h-full rounded-full bg-gold"
          style={{ left: `${pct(value[0])}%`, right: `${100 - pct(value[1])}%` }}
        />
      </div>
      {/* Min thumb */}
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value[0]}
        onChange={(e) => {
          const v = parseFloat(e.target.value);
          if (v <= value[1]) onChange([v, value[1]]);
        }}
        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
        style={{ zIndex: value[0] > max - step ? 5 : 3 }}
      />
      {/* Max thumb */}
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value[1]}
        onChange={(e) => {
          const v = parseFloat(e.target.value);
          if (v >= value[0]) onChange([value[0], v]);
        }}
        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
        style={{ zIndex: 4 }}
      />
      {/* Thumb markers */}
      <div
        className="absolute w-4 h-4 rounded-full bg-gold border-2 border-background shadow pointer-events-none"
        style={{ left: `calc(${pct(value[0])}% - 8px)` }}
      />
      <div
        className="absolute w-4 h-4 rounded-full bg-gold border-2 border-background shadow pointer-events-none"
        style={{ left: `calc(${pct(value[1])}% - 8px)` }}
      />
    </div>
  );
}

// ─── Single-handle slider ────────────────────────────────────────────────────

interface SingleSliderProps {
  min: number;
  max: number;
  step: number;
  value: number;
  onChange: (value: number) => void;
}

function SingleSlider({ min, max, step, value, onChange }: SingleSliderProps) {
  const pct = ((value - min) / (max - min)) * 100;

  return (
    <div className="relative h-5 flex items-center">
      <div className="absolute inset-x-0 h-1.5 rounded-full bg-secondary">
        <div className="absolute h-full rounded-full bg-gold" style={{ left: 0, right: `${100 - pct}%` }} />
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
        style={{ zIndex: 3 }}
      />
      <div
        className="absolute w-4 h-4 rounded-full bg-gold border-2 border-background shadow pointer-events-none"
        style={{ left: `calc(${pct}% - 8px)` }}
      />
    </div>
  );
}

// ─── Main component ──────────────────────────────────────────────────────────

const DocumentSearchResults: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [activeTab, setActiveTab] = useState<'court' | 'legislation'>('court');
  const [filtersOpen, setFiltersOpen] = useState(true);

  const assistantMessage = useAppSelector(selectDocumentSearchAssistantMessage);
  const filters = useAppSelector(state => state.documentSearch);
  const [hasFetchedDocuments, setHasFetchedDocuments] = useState(false);

  const documentEntities = useAppSelector(state => state.DBDocumentReducer.entities);
  const kbEntities = useAppSelector(state => state.DBKnowledgebaseReducer.entities);

  const [fetchRelevantDocuments] = useFetchRelevantDocumentsMutation();

  const aggregatedDocuments = useMemo(() => {
    if (!assistantMessage?.relevantDocumentIds) return [];
    return aggregateSearchResults(assistantMessage.relevantDocumentIds);
  }, [assistantMessage?.relevantDocumentIds]);

  useEffect(() => {
    setHasFetchedDocuments(false);
  }, [assistantMessage?._id]);

  useEffect(() => {
    if (assistantMessage?.relevantDocumentIds && !hasFetchedDocuments) {
      const documentIds = Object.keys(assistantMessage.relevantDocumentIds);
      if (documentIds.length > 0) {
        fetchRelevantDocuments({ documentIds });
        setHasFetchedDocuments(true);
      }
    }
  }, [assistantMessage?.relevantDocumentIds, hasFetchedDocuments, fetchRelevantDocuments]);

  const resultIsCourtMap = useMemo(() => {
    const map = new Map<string, boolean>();
    aggregatedDocuments.forEach(doc => {
      const document = documentEntities[doc.documentId] as any;
      if (document?.knowledgebaseId) {
        const kb = kbEntities[document.knowledgebaseId] as any;
        map.set(doc.documentId, kb?.isCourt === true);
      }
    });
    return map;
  }, [aggregatedDocuments, documentEntities, kbEntities]);

  const allFilteredResults = useMemo(() => {
    return getFilteredAndSortedResults(aggregatedDocuments, filters);
  }, [aggregatedDocuments, filters]);

  const filteredResults = useMemo(() => {
    return allFilteredResults.filter(r => {
      const isCourt = resultIsCourtMap.get(r.documentId) ?? false;
      return activeTab === 'court' ? isCourt : !isCourt;
    });
  }, [allFilteredResults, resultIsCourtMap, activeTab]);

  const courtCount = useMemo(
    () => allFilteredResults.filter(r => resultIsCourtMap.get(r.documentId) === true).length,
    [allFilteredResults, resultIsCourtMap]
  );
  const legislationCount = useMemo(
    () => allFilteredResults.filter(r => resultIsCourtMap.get(r.documentId) !== true).length,
    [allFilteredResults, resultIsCourtMap]
  );

  const availableKnowledgebases = useMemo(() => {
    if (aggregatedDocuments.length === 0) return [];
    const kbs = new Map<string, string>();
    aggregatedDocuments.forEach((doc) => {
      if (!kbs.has(doc.knowledgebaseId)) kbs.set(doc.knowledgebaseId, doc.knowledgebaseName);
    });
    return Array.from(kbs.entries()).map(([id, name]) => ({ id, name }));
  }, [aggregatedDocuments]);

  if (!assistantMessage) {
    return (
      <div className="text-center text-muted-foreground mt-8">
        <p className="text-sm">{t('components.documentSearch.noResultsAvailable', 'No results available')}</p>
      </div>
    );
  }

  const totalResults = aggregatedDocuments.length;
  const filteredCount = filteredResults.length;
  const activeTabCount = activeTab === 'court' ? courtCount : legislationCount;

  // Export handlers — unchanged
  const handleExportCSV = () => {
    const headers = ['Document Title', 'Knowledgebase', 'Relevance', 'Confidence', 'Score', 'Content Type', 'Key Terms'];
    const rows = filteredResults.map((r) => [
      r.documentTitle,
      r.knowledgebaseName,
      r.relevance,
      r.confidence ? r.confidence.toFixed(2) : 'N/A',
      r.score.toFixed(4),
      r.contentType,
      r.keyTerms?.join('; ') || ''
    ]);
    const csv = [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `document-search-results-${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleExportJSON = () => {
    const json = JSON.stringify(filteredResults, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `document-search-results-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Filter toggle helpers
  const toggleRelevance = (value: string) => {
    const next = filters.relevanceFilter.includes(value)
      ? filters.relevanceFilter.filter(v => v !== value)
      : [...filters.relevanceFilter, value];
    dispatch(setRelevanceFilter(next));
  };

  const toggleContentType = (value: string) => {
    const next = filters.contentTypeFilter.includes(value)
      ? filters.contentTypeFilter.filter(v => v !== value)
      : [...filters.contentTypeFilter, value];
    dispatch(setContentTypeFilter(next));
  };

  const toggleKnowledgebase = (value: string) => {
    const next = filters.knowledgebaseFilter.includes(value)
      ? filters.knowledgebaseFilter.filter(v => v !== value)
      : [...filters.knowledgebaseFilter, value];
    dispatch(setKnowledgebaseFilter(next));
  };

  const relevanceOptions: MultiSelectOption[] = [
    { value: 'highly_relevant', label: t('components.documentSearch.relevance.highly_relevant', 'Highly Relevant') },
    { value: 'moderately_relevant', label: t('components.documentSearch.relevance.moderately_relevant', 'Moderately Relevant') },
    { value: 'slightly_relevant', label: t('components.documentSearch.relevance.slightly_relevant', 'Slightly Relevant') },
    { value: 'not_relevant', label: t('components.documentSearch.relevance.not_relevant', 'Not Relevant') },
  ];

  const contentTypeOptions: MultiSelectOption[] = [
    { value: 'case_law', label: t('components.documentSearch.contentType.case_law', 'Case Law') },
    { value: 'statute', label: t('components.documentSearch.contentType.statute', 'Statute') },
    { value: 'regulation', label: t('components.documentSearch.contentType.regulation', 'Regulation') },
    { value: 'legal_analysis', label: t('components.documentSearch.contentType.legal_analysis', 'Legal Analysis') },
    { value: 'procedural', label: t('components.documentSearch.contentType.procedural', 'Procedural') },
    { value: 'other', label: t('components.documentSearch.contentType.other', 'Other') },
  ];

  const sortByOptions = [
    { value: 'relevance', label: t('components.documentSearch.sortBy.relevance', 'Relevance') },
    { value: 'confidence', label: t('components.documentSearch.sortBy.confidence', 'Confidence') },
    { value: 'score', label: t('components.documentSearch.sortBy.score', 'Search Score') },
    { value: 'documentTitle', label: t('components.documentSearch.sortBy.documentTitle', 'Document Title') },
    { value: 'knowledgebase', label: t('components.documentSearch.sortBy.knowledgebase', 'Knowledgebase') },
  ];

  return (
    <div className="max-w-[1400px] mx-auto p-4">

      {/* Header */}
      <div className="mb-6 flex justify-between items-center gap-4 flex-wrap">
        <div>
          <h2 className="text-xl font-semibold text-foreground">
            {t('components.documentSearch.searchResults', 'Search Results')}
          </h2>
          <p className="text-sm text-muted-foreground mt-0.5">
            {t('components.documentSearch.showingResults')
              .replace('{{filtered}}', filteredCount.toString())
              .replace('{{total}}', activeTabCount.toString())}
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={handleExportCSV}
            disabled={filteredCount === 0}
            className="gap-1.5 border-border hover:border-gold/40"
          >
            <Download size={14} />
            {t('common.exportCSV', 'Export CSV')}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={handleExportJSON}
            disabled={filteredCount === 0}
            className="gap-1.5 border-border hover:border-gold/40"
          >
            <Download size={14} />
            {t('common.exportJSON', 'Export JSON')}
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-6">
        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'court' | 'legislation')}>
          <TabsList className="w-full">
            <TabsTrigger value="court" className="flex-1">
              {t('components.documentSearch.courtDecisionsTab')}
              <span className="ml-1.5 text-xs opacity-60">({courtCount})</span>
            </TabsTrigger>
            <TabsTrigger value="legislation" className="flex-1">
              {t('components.documentSearch.legislationTab')}
              <span className="ml-1.5 text-xs opacity-60">({legislationCount})</span>
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Filters panel */}
      <div className="mb-6 rounded-2xl border border-border bg-card/60 overflow-hidden">
        {/* Panel header / toggle */}
        <button
          onClick={() => setFiltersOpen(o => !o)}
          className="w-full flex items-center gap-2 px-5 py-3.5 text-left hover:bg-secondary/40 transition-colors"
        >
          <SlidersHorizontal size={16} className="text-gold" />
          <span className="text-sm font-semibold text-foreground flex-1">
            {t('components.documentSearch.filtersAndSort', 'Filters & Sorting')}
          </span>
          <ChevronDown
            size={16}
            className={`text-muted-foreground transition-transform duration-200 ${filtersOpen ? 'rotate-180' : ''}`}
          />
        </button>

        {filtersOpen && (
          <div className="px-5 pb-5 pt-2 border-t border-border">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

              {/* Relevance filter */}
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  {t('components.documentSearch.filterLabels.relevance', 'Relevance')}
                </label>
                <MultiSelect
                  placeholder={t('components.documentSearch.filterLabels.relevance', 'Relevance')}
                  options={relevanceOptions}
                  selected={filters.relevanceFilter}
                  onToggle={toggleRelevance}
                />
              </div>

              {/* Content type filter */}
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  {t('components.documentSearch.filterLabels.contentType', 'Content Type')}
                </label>
                <MultiSelect
                  placeholder={t('components.documentSearch.filterLabels.contentType', 'Content Type')}
                  options={contentTypeOptions}
                  selected={filters.contentTypeFilter}
                  onToggle={toggleContentType}
                />
              </div>

              {/* Knowledgebase filter */}
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  {t('components.documentSearch.filterLabels.knowledgebase', 'Knowledgebase')}
                </label>
                <MultiSelect
                  placeholder={t('components.documentSearch.filterLabels.knowledgebase', 'Knowledgebase')}
                  options={availableKnowledgebases.map(kb => ({ value: kb.id, label: kb.name }))}
                  selected={filters.knowledgebaseFilter}
                  onToggle={toggleKnowledgebase}
                />
              </div>

              {/* Sort by + direction */}
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  {t('components.documentSearch.filterLabels.sortBy', 'Sort By')}
                </label>
                <div className="flex gap-2">
                  <select
                    value={filters.sortBy}
                    onChange={(e) => dispatch(setSortBy(e.target.value))}
                    className="flex-1 h-9 px-3 rounded-lg border border-border bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-gold/40 hover:border-gold/50 transition-colors"
                  >
                    {sortByOptions.map(o => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                  <button
                    title={filters.sortDirection === 'asc'
                      ? t('common.ascending', 'Ascending')
                      : t('common.descending', 'Descending')}
                    onClick={() => dispatch(toggleSortDirection())}
                    className="h-9 w-9 flex items-center justify-center rounded-lg border border-border bg-background hover:border-gold/50 hover:text-gold transition-colors text-muted-foreground"
                  >
                    {filters.sortDirection === 'asc' ? <ArrowUp size={15} /> : <ArrowDown size={15} />}
                  </button>
                </div>
              </div>

              {/* Confidence range slider */}
              <div className="space-y-2">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  {t('components.documentSearch.confidenceRange', 'Confidence Range')}:
                  <span className="ml-1 text-foreground font-semibold normal-case tracking-normal">
                    {filters.confidenceRange[0].toFixed(2)} – {filters.confidenceRange[1].toFixed(2)}
                  </span>
                </p>
                <RangeSlider
                  min={0}
                  max={1}
                  step={0.05}
                  value={filters.confidenceRange as [number, number]}
                  onChange={(v) => dispatch(setConfidenceRange(v))}
                />
                <div className="flex justify-between text-[10px] text-muted-foreground">
                  <span>0</span><span>0.5</span><span>1</span>
                </div>
              </div>

              {/* Score threshold slider */}
              <div className="space-y-2">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  {t('components.documentSearch.scoreThreshold', 'Minimum Score')}:
                  <span className="ml-1 text-foreground font-semibold normal-case tracking-normal">
                    {filters.scoreThreshold.toFixed(2)}
                  </span>
                </p>
                <SingleSlider
                  min={0}
                  max={1}
                  step={0.05}
                  value={filters.scoreThreshold}
                  onChange={(v) => dispatch(setScoreThreshold(v))}
                />
                <div className="flex justify-between text-[10px] text-muted-foreground">
                  <span>0</span><span>0.5</span><span>1</span>
                </div>
              </div>

              {/* Reset */}
              <div className="md:col-span-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => dispatch(resetFilters())}
                  className="gap-1.5 border-border hover:border-gold/40 hover:text-gold"
                >
                  <RotateCcw size={14} />
                  {t('common.resetFilters', 'Reset Filters')}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Results */}
      {filteredResults.length > 0 ? (
        <div className="space-y-3">
          {filteredResults.map((result) => (
            <DocumentSearchResultItem
              key={result.documentId}
              documentId={result.documentId}
              relevance={result.relevance}
              contentType={result.contentType}
              score={result.score}
              confidence={result.confidence}
              reasoning={result.reasoning}
              keyTerms={result.keyTerms}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-12 px-6 rounded-2xl border border-dashed border-border text-muted-foreground">
          <p className="text-sm">
            {totalResults === 0
              ? t('components.documentSearch.noDocumentsFound', 'No documents found for this search')
              : t('components.documentSearch.noResultsMatchFilters', 'No results match the current filters')}
          </p>
        </div>
      )}
    </div>
  );
};

export default DocumentSearchResults;
